import { useState, useCallback } from 'react';

/**
 * Shared hook for MOV → MP4 conversion via AWS MediaConvert.
 * Wraps the convert + poll pattern so any upload tool can await conversion inline.
 */
export function useMovConversion() {
  const [isConverting, setIsConverting] = useState(false);

  /** Returns true if the file needs conversion (not .mp4) */
  const needsConversion = useCallback((fileName: string): boolean => {
    return !fileName.toLowerCase().endsWith('.mp4');
  }, []);

  /**
   * Convert a non-MP4 file (already uploaded to S3) to MP4 via MediaConvert.
   * Polls every 5s until complete. Returns the converted outputKey + downloadUrl.
   * @param s3Key - The S3 key of the uploaded file
   * @param bucket - Optional source bucket (defaults to hey-joe-video-rotator)
   */
  const convert = useCallback(async (
    s3Key: string,
    bucket?: string
  ): Promise<{ outputKey: string; downloadUrl: string }> => {
    setIsConverting(true);
    try {
      // Start conversion job
      const body: Record<string, string> = { s3Key };
      if (bucket) body.bucket = bucket;

      const res = await fetch('/api/convert-mov', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to start conversion');
      const { jobId } = await res.json();

      // Poll until complete
      return await new Promise((resolve, reject) => {
        const poll = async () => {
          try {
            const statusRes = await fetch(`/api/convert-mov/status/${jobId}`);
            if (!statusRes.ok) throw new Error('Failed to check conversion status');
            const data = await statusRes.json();

            if (data.status === 'completed') {
              resolve({ outputKey: data.outputKey, downloadUrl: data.downloadUrl });
            } else if (data.status === 'failed') {
              reject(new Error(data.error || 'Conversion failed'));
            } else {
              setTimeout(poll, 5000);
            }
          } catch (err) {
            reject(err);
          }
        };
        poll();
      });
    } finally {
      setIsConverting(false);
    }
  }, []);

  return { needsConversion, convert, isConverting };
}
